import "./assignBuilderList.css";
import React, { useEffect, useState } from "react";
import SearchBar from "../Main/searchBar";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ReactComponent as BackIcon } from "../../Assets/Categories/BackIcon.svg";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import {
  getBuildersByFranchise,
  revokeBuilderFromFranchise,
} from "../../Redux/Actions/franchiseAction";
import { setSelection } from "../../Redux/Reducers/franchiseReducer";

import { Dropdown, Menu, Button, Pagination } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";

const AssignBuilderList = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [builderList, setBuilderList] = useState([]);
  const [isLoadingBuilders, setIsLoadingBuilders] = useState(false);
  const [selectedAcls, setSelectedAcls] = useState(null); // Track selected ACL list for popup
  const [isAclsPopupOpen, setIsAclsPopupOpen] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const handleProjectsListClick = (builderId) => {
    dispatch(setSelection({ builderId, franchiseId: id }));
    navigate("/franchise/projects_list");
  };

  useEffect(() => {
    if (id) {
      fetchBuilderList(pagination.current, pagination.pageSize);
    }
  }, [id]);

  const fetchBuilderList = async (page = 1, limit = 10) => {
    setIsLoadingBuilders(true);
    try {
      const filterData = { page, limit };
      const res = await dispatch(getBuildersByFranchise(id, filterData));
      if (res && res.data && Array.isArray(res.data)) {
        setBuilderList(res.data);
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize: limit,
          total: res.total || 0,
        }));
        if (res.data.length === 0) {
          toast("No builders assigned to this franchise");
        }
      } else {
        setBuilderList([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        toast("No builders assigned to this franchise");
      }
    } catch (error) {
      toast.error("Failed to fetch builders");
    } finally {
      setIsLoadingBuilders(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No Data";
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleRevokeClick = async (builder) => {
    if (!id || !builder.builder_id) {
      toast.error("Invalid franchise or builder ID");
      return;
    }
    const payload = {
      franchise_id: id,
      builder_id: builder.builder_id,
    };
    try {
      const res = await dispatch(revokeBuilderFromFranchise(payload));
      if (res && res.statusCode === 200) {
        toast.success("Builder revoked successfully");
        fetchBuilderList(pagination.current, pagination.pageSize); // Refresh list after revoking
      } else {
        toast.error(res.message || "Failed to revoke builder");
      }
    } catch {
      toast.error("Failed to revoke builder");
    }
  };

  const openAclsPopup = (acls) => {
    if (acls && acls.length > 0) {
      setSelectedAcls(acls);
      setIsAclsPopupOpen(true);
    }
  };

  const closeAclsPopup = () => {
    setIsAclsPopupOpen(false);
    setSelectedAcls(null);
  };

  const onPaginationChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));
    fetchBuilderList(page, pageSize);
  };

  return (
    <div className="grid-container">
      <div
        className="padding-20"
        style={{ gridColumn: "3/8", marginBottom: 20 }}
      >
        <SearchBar style={{ width: "max-content" }} />
      </div>

      <div
        style={{
          gridColumn: "span 7",
          marginBottom: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingRight: "16px",
        }}
      >
        {/* Left group: Back Icon + Heading */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexShrink: 0,
          }}
        >
          <Link to="/franchise">
            <BackIcon style={{ cursor: "pointer", width: 80, height: 80 }} />
          </Link>
          <h3 style={{ margin: 0, fontWeight: 600, color: "#181E2E" }}>
            Builders Assigned
          </h3>
        </div>

        <Link
          to={`/franchise/assign-builder/${id}`}
          style={{
            display: "inline-flex",
            justifyContent: "center",
            alignItems: "center",
            height: 66,
            width: 200,
            backgroundColor: "#181E2E",
            color: "#fff",
            textDecoration: "none",
            fontWeight: "bold",
            borderRadius: 32,
            cursor: "pointer",
          }}
          className="assign-builder-link"
        >
          Builder List
        </Link>
      </div>

      <div className="overall-container">
        <div
          className={`builder-list-wrapper ${
            builderList.length === 0 && !isLoadingBuilders ? "centered" : ""
          }`}
        >
          {isLoadingBuilders ? (
            <div className="loader">Loading builders...</div>
          ) : builderList.length > 0 ? (
            <>
              <table className="builder-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Builder ID</th>
                    <th>Brand Name</th>
                    <th>Contact Number</th>
                    <th>Email</th>
                    <th>ACLs</th>
                    <th>Created At</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {builderList.map((builder, index) => (
                    <tr key={builder._id}>
                      <td>{(pagination.current - 1) * pagination.pageSize + index + 1}</td>
                      <td>{builder.builder_code ?? "No Data"}</td>
                      <td>{builder.brand_name ?? builder.builder_id ?? "No Data"}</td>
                      <td>{builder.primary_contact_number ?? "No Data"}</td>
                      <td>{builder.primary_email ?? "No Data"}</td>
                      <td>
                        {builder.acls && builder.acls.length > 0 ? (
                          <span
                            onClick={() => openAclsPopup(builder.acls)}
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                              color: "white",
                            }}
                          >
                            View ACLs
                          </span>
                        ) : (
                          "No ACLs"
                        )}
                      </td>
                      <td>{formatDate(builder.created_at)}</td>
                      <td>
                        <Dropdown
                          overlay={
                            <Menu>
                              <Menu.Item
                                key="revoke"
                                onClick={() => handleRevokeClick(builder)}
                              >
                                Revoke
                              </Menu.Item>
                              <Menu.Item
                                key="Projects List"
                                onClick={() =>
                                  handleProjectsListClick(builder.builder_id)
                                }
                              >
                                Projects List
                              </Menu.Item>
                              <Menu.Item key="Assign Projects">
                                <Link to={`/franchise/assign_projects`}>
                                  Assigned Projects
                                </Link>
                              </Menu.Item>
                            </Menu>
                          }
                          trigger={["click"]}
                        >
                          <Button shape="square" icon={<EllipsisOutlined />} />
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                style={{ marginTop: 16, textAlign: "right" }}
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger
                pageSizeOptions={["5","10", "20", "50", "100"]}
                onChange={onPaginationChange}
                onShowSizeChange={onPaginationChange}
              />
            </>
          ) : (
            <div
              style={{
                textAlign: "center",
                fontSize: 18,
                color: "rgba(255,255,255,0.7)",
                padding: 30,
              }}
            >
              No builders assigned to this franchise.
            </div>
          )}
        </div>
      </div>

      {isAclsPopupOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={closeAclsPopup}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: 20,
              borderRadius: 8,
              width: 300,
              maxHeight: "80vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4>ACL List</h4>
            <ul>
              {selectedAcls.map((acl) => (
                <li key={acl._id}>
                  <strong>
                    {acl.name.replace(/\b\w/g, (char) => char.toUpperCase())}
                  </strong>
                  :{" "}
                  {acl.access
                    .map((item) =>
                      item.replace(/\b\w/g, (char) => char.toUpperCase())
                    )
                    .join(", ")}
                </li>
              ))}
            </ul>

            <button onClick={closeAclsPopup} style={{ marginTop: 10 }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignBuilderList;
